import { GameEngine } from '../game/gameEngine';
import { CellState, GameState } from '../types';
import '../styles/main.css';

/**
 * Web UI for the Gomoku/Caro game
 */
export class GameUI {
  private game: GameEngine;
  private boardElement: HTMLElement | null = null;
  private messageElement: HTMLElement | null = null;
  private currentPlayerElement: HTMLElement | null = null;
  private statsElement: HTMLElement | null = null;
  private vietnameseRulesCheckbox: HTMLInputElement | null = null;

  constructor(containerSelector: string = '#app') {
    const container = document.querySelector(containerSelector);
    if (!container) {
      throw new Error(`Container ${containerSelector} not found`);
    }

    this.createUI(container as HTMLElement);
    this.game = new GameEngine('Player 1', 'Player 2', 30, true);
    this.render();
  }

  /**
   * Creates the UI structure
   */
  private createUI(container: HTMLElement): void {
    container.innerHTML = `
      <div class="container">
        <div class="side-panel">
          <div>
            <h1>🎮 Gomoku - Caro</h1>
            <p class="subtitle">Get 5 in a row with at least one open end to win!</p>
          </div>

          <div class="settings">
            <label>
              <input type="checkbox" id="vietnameseRules" checked>
              Vietnamese Caro Rules
            </label>
          </div>

          <div class="game-info">
            <div class="player-info">
              <span class="player-x">Player 1 (🞩)</span>
            </div>
            <div class="player-info current-player" id="currentPlayer">
              Current: 🞩
            </div>
            <div class="player-info">
              <span class="player-o">Player 2 (🞅)</span>
            </div>
          </div>

          <div class="stats" id="stats">
            Moves: 0
          </div>

          <div class="message" id="message"></div>

          <div class="controls">
            <button class="reset-btn" id="resetBtn">New Game</button>
          </div>
        </div>

        <div class="board-area">
          <div class="board-container">
            <div class="board" id="board"></div>
          </div>
        </div>
      </div>
    `;

    // Store references
    this.boardElement = document.getElementById('board');
    this.messageElement = document.getElementById('message');
    this.currentPlayerElement = document.getElementById('currentPlayer');
    this.statsElement = document.getElementById('stats');
    this.vietnameseRulesCheckbox = document.getElementById(
      'vietnameseRules'
    ) as HTMLInputElement;

    // Add event listeners
    document
      .getElementById('resetBtn')
      ?.addEventListener('click', () => this.resetGame());
    this.vietnameseRulesCheckbox?.addEventListener('change', () =>
      this.resetGame()
    );
  }

  /**
   * Renders the game board
   */
  private render(): void {
    if (!this.boardElement) return;

    const boardSize = this.game.getBoard().getSize();

    // Calculate optimal cell size to fit screen (accounting for side panel)
    const maxBoardHeight = window.innerHeight - 40; // Small padding
    const maxBoardWidth = window.innerWidth - 320; // Side panel (280px) + padding
    const maxCellSize = Math.floor(
      Math.min(maxBoardHeight, maxBoardWidth) / boardSize
    );
    const cellSize = Math.max(18, Math.min(maxCellSize, 30)); // Between 18px and 30px

    this.boardElement.style.gridTemplateColumns = `repeat(${boardSize}, ${cellSize}px)`;
    this.boardElement.style.gridTemplateRows = `repeat(${boardSize}, ${cellSize}px)`;
    this.boardElement.innerHTML = '';

    const grid = this.game.getBoard().getGrid();

    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        const cell = document.createElement('button');
        cell.className = 'cell';
        cell.dataset.row = row.toString();
        cell.dataset.col = col.toString();

        const cellValue = grid[row][col];
        if (cellValue !== CellState.EMPTY) {
          cell.textContent = cellValue === CellState.PLAYER_X ? '🞩' : '🞅';
          cell.classList.add('occupied');
          cell.classList.add(cellValue === CellState.PLAYER_X ? 'x' : 'o');
        }

        cell.addEventListener('click', () => this.handleCellClick(row, col));
        this.boardElement!.appendChild(cell);
      }
    }

    this.updateStatus();
  }

  /**
   * Handles cell click
   */
  private handleCellClick(row: number, col: number): void {
    if (this.game.isGameOver()) {
      return;
    }

    const success = this.game.makeMove(row, col);
    if (success) {
      this.render();

      // Highlight winning row if game is won
      if (this.game.isGameOver() && this.game.getWinner()) {
        this.highlightWinningRow(row, col);
      } else {
        // Highlight threats (4 in a row) for both players
        this.highlightThreats();
      }
    }
  }

  /**
   * Highlights the winning row
   */
  private highlightWinningRow(lastRow: number, lastCol: number): void {
    const currentPlayer = this.game.getWinner();
    if (!currentPlayer) return;

    const board = this.game.getBoard();
    const directions = [
      { rowDelta: 0, colDelta: 1 }, // Horizontal
      { rowDelta: 1, colDelta: 0 }, // Vertical
      { rowDelta: 1, colDelta: 1 }, // Diagonal \
      { rowDelta: 1, colDelta: -1 }, // Diagonal /
    ];

    for (const direction of directions) {
      const winningCells: { row: number; col: number }[] = [
        { row: lastRow, col: lastCol },
      ];

      // Check positive direction
      let r = lastRow + direction.rowDelta;
      let c = lastCol + direction.colDelta;
      while (
        board.isValidPosition(r, c) &&
        board.getCell(r, c) === currentPlayer.mark
      ) {
        winningCells.push({ row: r, col: c });
        r += direction.rowDelta;
        c += direction.colDelta;
      }

      // Check negative direction
      r = lastRow - direction.rowDelta;
      c = lastCol - direction.colDelta;
      while (
        board.isValidPosition(r, c) &&
        board.getCell(r, c) === currentPlayer.mark
      ) {
        winningCells.unshift({ row: r, col: c });
        r -= direction.rowDelta;
        c -= direction.colDelta;
      }

      // If we found 5 or more in a row, highlight them
      if (winningCells.length >= 5) {
        winningCells.forEach((pos) => {
          const cell = document.querySelector(
            `[data-row="${pos.row}"][data-col="${pos.col}"]`
          );
          cell?.classList.add('winning');
        });
        break;
      }
    }
  }

  /**
   * Highlights threats (4 in a row) for both players
   */
  private highlightThreats(): void {
    const board = this.game.getBoard();
    const boardSize = board.getSize();
    const directions = [
      { rowDelta: 0, colDelta: 1 }, // Horizontal
      { rowDelta: 1, colDelta: 0 }, // Vertical
      { rowDelta: 1, colDelta: 1 }, // Diagonal \
      { rowDelta: 1, colDelta: -1 }, // Diagonal /
    ];

    // Remove all existing threat highlights
    document.querySelectorAll('.cell.threat').forEach((cell) => {
      cell.classList.remove('threat');
    });

    // Check for threats from all positions
    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        const cellValue = board.getCell(row, col);
        if (cellValue === CellState.EMPTY) continue;

        // Check each direction for this cell
        for (const direction of directions) {
          const threateningCells: { row: number; col: number }[] = [
            { row, col },
          ];

          // Check positive direction
          let r = row + direction.rowDelta;
          let c = col + direction.colDelta;
          while (
            board.isValidPosition(r, c) &&
            board.getCell(r, c) === cellValue
          ) {
            threateningCells.push({ row: r, col: c });
            r += direction.rowDelta;
            c += direction.colDelta;
          }

          // Check negative direction
          r = row - direction.rowDelta;
          c = col - direction.colDelta;
          while (
            board.isValidPosition(r, c) &&
            board.getCell(r, c) === cellValue
          ) {
            threateningCells.unshift({ row: r, col: c });
            r -= direction.rowDelta;
            c -= direction.colDelta;
          }

          // If we found exactly 4 in a row, highlight them as a threat
          if (threateningCells.length === 4) {
            threateningCells.forEach((pos) => {
              const cell = document.querySelector(
                `[data-row="${pos.row}"][data-col="${pos.col}"]`
              );
              cell?.classList.add('threat');
            });
          }
        }
      }
    }
  }

  /**
   * Updates game status display
   */
  private updateStatus(): void {
    const currentPlayer = this.game.getCurrentPlayer();
    const gameState = this.game.getGameState();
    const moveCount = this.game.getMoveHistory().length;

    // Update current player
    if (this.currentPlayerElement) {
      const symbol = currentPlayer.mark === CellState.PLAYER_X ? '🞩' : '🞅';
      this.currentPlayerElement.textContent = `Current: ${symbol}`;
    }

    // Update stats
    if (this.statsElement) {
      this.statsElement.textContent = `Moves: ${moveCount}`;
    }

    // Update message
    if (this.messageElement) {
      this.messageElement.className = 'message';

      if (
        gameState === GameState.PLAYER_X_WON ||
        gameState === GameState.PLAYER_O_WON
      ) {
        const winner = this.game.getWinner();
        this.messageElement.textContent = `🎉 ${winner?.name} (${winner?.mark}) wins! 🎉`;
        this.messageElement.classList.add('show', 'win');
      } else if (gameState === GameState.DRAW) {
        this.messageElement.textContent = 'Game ended in a draw!';
        this.messageElement.classList.add('show', 'draw');
      }
    }
  }

  /**
   * Resets the game
   */
  private resetGame(): void {
    const requireOpenEnd = this.vietnameseRulesCheckbox?.checked ?? true;
    this.game = new GameEngine('Player 1', 'Player 2', 30, requireOpenEnd);
    this.render();
  }
}
